import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { SampleSubType, SampleType } from '@resistapp/common/sampling-point-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellFocusedEvent,
  type CellKeyDownEvent,
  type CellRange,
  type CellSelectionOptions,
  type CellValueChangedEvent,
  type ColDef,
  type GridApi,
  type ProcessCellForExportParams,
  type ValueFormatterParams,
  type ValueParserParams,
} from 'ag-grid-community';
import { ClipboardModule } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import '../../sample-sheet-view/sample-sheet-grid.css';

import {
  applyDerivedLabDnaStepDilutionFields,
  formatLabDnaStepSampleSubType,
  formatLabDnaStepSampleType,
  isLabDnaStepFieldEnabledForServices,
  isLabDnaStepFieldVisibleToCustomer,
  labDnaStepColumnMetadata,
  labDnaStepDilutionFieldKeys,
  labDnaStepExtractionFieldKeys,
  labDnaStepQaFieldKeys,
  labWorkbookServiceChoices,
  normalizeLabDnaStepEditableFields,
  normalizeLabWorkbookServices,
  normalizeNullableDateString,
  normalizeNullableNumber,
  normalizeNullableText,
  type LabDnaStepColumnMeta,
  type LabDnaStepDisplayFieldKey,
  type LabDnaStepRow,
  type LabDnaStepRowUpdate,
  type LabWorkbookService,
} from '@resistapp/common/lab-dna-step';
import { LabWorkbookStatus } from '@resistapp/common/types';
import { useToastWithoutDebounce } from '../../../hooks/use-resistomap-toast';
import { moveFocusRelative, resolveGridColumnFieldId } from '../../../utils/ag-grid-sheet-utils';
import { markLabDnaStepReady, saveLabDnaStep, useLabDnaStep } from '../api/use-lab-dna-step';
import type { LabWorkbookSummary } from '../api/use-lab-workbook';
import { LabPageHeader } from '../components/lab-page-header';
import { useLabLayoutContext } from '../lab-layout';
import {
  shouldHighlightDnaDilutionDesiredFinalVolumeCell,
  shouldPreserveLocalWorkbookServices,
} from './dna-extraction-page-state-utils';

ModuleRegistry.registerModules([AllCommunityModule, ClipboardModule]);

const GRID_CLASS_NAME = 'lab-dna-step-grid';
const SAVE_DEBOUNCE_MS = 500;
const GROUP_STYLES = {
  tubeLabel: {
    background: '#edf2f7',
    activeBackground: '#e3ebf7',
    activeCellBackground: '#dbe6f4',
  },
  identity: {
    background: '#f7fafc',
    activeBackground: '#edf5fc',
    activeCellBackground: '#e6f0fb',
  },
  extraction: {
    background: '#fff7fa',
    activeBackground: '#ffedf4',
    activeCellBackground: '#ffe4ef',
  },
  qa: {
    background: '#fffaf0',
    activeBackground: '#fff3e4',
    activeCellBackground: '#ffedcc',
  },
  dilution: {
    background: '#fff3d8',
    activeBackground: '#ffe8b8',
    activeCellBackground: '#ffe39a',
  },
} as const;

type DnaColumnField = LabDnaStepDisplayFieldKey;

const visibleIdentityFieldKeys: readonly DnaColumnField[] = ['sampleNumber', 'sampleDescription', 'detailedSampleType'];

function toErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback;
}

function areServicesEqual(
  left: readonly LabWorkbookService[] | null | undefined,
  right: readonly LabWorkbookService[] | null | undefined,
): boolean {
  return JSON.stringify(normalizeLabWorkbookServices(left)) === JSON.stringify(normalizeLabWorkbookServices(right));
}

function getServicesChoiceValue(services: readonly LabWorkbookService[] | null | undefined): string {
  const normalized = normalizeLabWorkbookServices(services);
  if (!normalized) {
    return '';
  }
  return labWorkbookServiceChoices.find(choice => areServicesEqual(choice.services, normalized))?.value ?? '';
}

function getServicesForChoiceValue(value: string): LabWorkbookService[] | null {
  const choice = labWorkbookServiceChoices.find(candidate => candidate.value === value);
  return normalizeLabWorkbookServices(choice?.services);
}

function isDnaStepFieldVisible(
  fieldKey: LabDnaStepDisplayFieldKey,
  services: readonly LabWorkbookService[] | null | undefined,
  customerOnly: boolean,
): boolean {
  if (!isLabDnaStepFieldEnabledForServices(fieldKey, services)) {
    return false;
  }
  return !customerOnly || isLabDnaStepFieldVisibleToCustomer(fieldKey, services);
}

function syncWorkbookServices(
  queryClient: ReturnType<typeof useQueryClient>,
  workbookId: string,
  services: LabWorkbookService[] | null,
): void {
  queryClient.setQueryData<LabWorkbookSummary | undefined>(['labWorkbook', workbookId], existing =>
    existing
      ? {
          ...existing,
          workbook: {
            ...existing.workbook,
            services,
          },
        }
      : existing,
  );
}

function formatAcceptedRange(meta: LabDnaStepColumnMeta): string | null {
  if (!meta.acceptedRange) {
    return null;
  }

  const range = meta.acceptedRange;
  switch (range.kind) {
    case 'between': {
      const bounds = range.bounds ?? '[]';
      const openLeft = bounds.startsWith('(') ? '>' : '>=';
      const openRight = bounds.endsWith(')') ? '<' : '<=';
      return `Accepted range: ${openLeft} ${range.min}, ${openRight} ${range.max}`;
    }
    case 'gt':
    case 'gte':
      return `Accepted range: ${range.kind === 'gt' ? '>' : '>='} ${range.min}`;
    case 'lt':
    case 'lte':
      return `Accepted range: ${range.kind === 'lt' ? '<' : '<='} ${range.max}`;
  }
}

function toSampleType(value: unknown): SampleType | null {
  return typeof value === 'string' && Object.values(SampleType).includes(value as SampleType)
    ? (value as SampleType)
    : null;
}

function toSampleSubType(value: unknown): SampleSubType | null {
  return typeof value === 'string' && Object.values(SampleSubType).includes(value as SampleSubType)
    ? (value as SampleSubType)
    : null;
}

function buildHeaderTooltip(fieldKey: DnaColumnField): string | undefined {
  const meta = labDnaStepColumnMetadata[fieldKey];
  const pieces = [meta.tooltip ?? null, formatAcceptedRange(meta)].filter((piece): piece is string => Boolean(piece));
  return pieces.length > 0 ? pieces.join('\n') : undefined;
}

function buildBaseCellClass(fieldKey: DnaColumnField): string {
  if (fieldKey === 'sampleNumber') {
    return 'sample-sheet-row-index-cell dna-tube-label-cell';
  }
  const meta = labDnaStepColumnMetadata[fieldKey];
  const classes = [`dna-${meta.group}-cell`];
  if (!meta.editable) {
    classes.push('dna-read-only-cell');
  }
  if (fieldKey === 'dilutionSampleVolumeUl' || fieldKey === 'dilutionWaterAddedUl') {
    classes.push('dna-derived-cell');
  }
  return classes.join(' ');
}

function buildHeaderClass(fieldKey: DnaColumnField): string | undefined {
  if (fieldKey === 'sampleNumber') {
    return 'sample-sheet-row-index-header dna-tube-label-header';
  }
  const { group } = labDnaStepColumnMetadata[fieldKey];
  if (group === 'extraction' || group === 'qa' || group === 'dilution') {
    return `dna-${group}-header`;
  }
  return undefined;
}

function formatNumberValue(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '';
  }
  const normalized = Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, '');
  return normalized;
}

function formatCellValueFromRaw(fieldKey: DnaColumnField, value: unknown, row: LabDnaStepRow | undefined): string {
  const meta = labDnaStepColumnMetadata[fieldKey];
  switch (meta.kind) {
    case 'number':
      return formatNumberValue(value);
    case 'date':
      return typeof value === 'string' ? value : '';
    case 'sampleType':
      return formatLabDnaStepSampleType(toSampleType(value));
    case 'sampleSubType': {
      const sampleType = toSampleType(row?.sampleType);
      const detailedSampleType = toSampleSubType(value);
      return formatLabDnaStepSampleSubType(sampleType ?? null, detailedSampleType);
    }
    default:
      return typeof value === 'string' ? value : '';
  }
}

function formatCellValue(fieldKey: DnaColumnField, params: ValueFormatterParams<LabDnaStepRow>): string {
  return formatCellValueFromRaw(fieldKey, params.value, params.data);
}

function normalizeParsedCellValue(fieldKey: DnaColumnField, value: unknown): string | number | null {
  const meta = labDnaStepColumnMetadata[fieldKey];
  switch (meta.kind) {
    case 'number':
      return normalizeNullableNumber(value);
    case 'date':
      return normalizeNullableDateString(value);
    default:
      return normalizeNullableText(value);
  }
}

function parseCellValue(fieldKey: DnaColumnField, params: ValueParserParams<LabDnaStepRow>): string | number | null {
  return normalizeParsedCellValue(fieldKey, params.newValue);
}

function normalizeComparableCellValue(fieldKey: DnaColumnField, value: unknown): string | number | null {
  const meta = labDnaStepColumnMetadata[fieldKey];
  switch (meta.kind) {
    case 'number':
      return normalizeNullableNumber(value);
    case 'date':
      return normalizeNullableDateString(value);
    default:
      return normalizeNullableText(value);
  }
}

function buildRowUpdatePayload(row: LabDnaStepRow): LabDnaStepRowUpdate {
  return {
    sampleId: row.sampleId,
    baseSampleUpdatedAt: row.baseSampleUpdatedAt,
    ...applyDerivedLabDnaStepDilutionFields(normalizeLabDnaStepEditableFields(row)),
  };
}

function getFocusedSampleId(event: CellFocusedEvent<LabDnaStepRow>): number | null {
  if (event.rowPinned != null || event.rowIndex == null) {
    return null;
  }
  return event.api.getDisplayedRowAtIndex(event.rowIndex)?.data?.sampleId ?? null;
}

function buildFieldColumn(
  fieldKey: DnaColumnField,
  options: {
    disableEditing: boolean;
    pendingRowIds: Set<number>;
    services: readonly LabWorkbookService[] | null | undefined;
  },
): ColDef<LabDnaStepRow> {
  const meta = labDnaStepColumnMetadata[fieldKey];
  const isTubeLabelColumn = fieldKey === 'sampleNumber';
  return {
    field: fieldKey,
    headerName: isTubeLabelColumn ? 'Tube\nLabel' : meta.header,
    headerTooltip: buildHeaderTooltip(fieldKey),
    width: isTubeLabelColumn ? 92 : meta.width,
    minWidth: isTubeLabelColumn ? 92 : meta.width ? Math.min(meta.width, 140) : 120,
    maxWidth: isTubeLabelColumn ? 92 : undefined,
    pinned: meta.pinned,
    editable: params =>
      Boolean(
        meta.editable &&
          !options.disableEditing &&
          params.data?.sampleEditable &&
          !options.pendingRowIds.has(params.data.sampleId),
      ),
    singleClickEdit: false,
    resizable: !isTubeLabelColumn,
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
    suppressMovable: isTubeLabelColumn,
    headerClass: buildHeaderClass(fieldKey),
    cellClass: params => {
      const classes = [buildBaseCellClass(fieldKey)];
      if (!meta.editable || !params.data?.sampleEditable || options.disableEditing) {
        classes.push('dna-read-only-cell');
      }
      return classes.join(' ');
    },
    cellClassRules:
      fieldKey === 'dilutionDesiredFinalVolumeUl'
        ? {
            'dna-dilution-desired-final-volume-warning-cell': params =>
              shouldHighlightDnaDilutionDesiredFinalVolumeCell(params.data, options.services),
          }
        : undefined,
    valueFormatter: params => formatCellValue(fieldKey, params),
    valueParser: meta.editable ? params => parseCellValue(fieldKey, params) : undefined,
  };
}

export function DnaExtractionPage() {
  const { workbookQuery, workbookId, viewerAccess, readOnly } = useLabLayoutContext();
  const workbookData = workbookQuery.data;
  const workbook = workbookData?.workbook ?? null;
  const workbookSupportsDnaStep =
    workbookData != null &&
    workbookData.workbook.status !== LabWorkbookStatus.LEGACY &&
    !workbookData.legacyImport.imported;
  const showToast = useToastWithoutDebounce();
  const queryClient = useQueryClient();
  const gridRef = useRef<AgGridReact<LabDnaStepRow>>(null);
  const dnaStepQuery = useLabDnaStep(workbookId, {
    enabled: Boolean(workbookId && viewerAccess.canViewDnaQc && workbookSupportsDnaStep),
  });

  const [rows, setRows] = useState<LabDnaStepRow[]>([]);
  const rowsRef = useRef<LabDnaStepRow[]>([]);
  const [services, setServices] = useState<LabWorkbookService[] | null>(null);
  const [servicesSaving, setServicesSaving] = useState(false);
  const servicesRef = useRef<LabWorkbookService[] | null>(null);
  const servicesSavingRef = useRef(false);
  const servicesVersionRef = useRef(0);
  const [dirtyRowIds, setDirtyRowIds] = useState<number[]>([]);
  const [pendingRowIds, setPendingRowIds] = useState<number[]>([]);
  const [lastSaveError, setLastSaveError] = useState<string | null>(null);
  const [activeCellSampleId, setActiveCellSampleId] = useState<number | null>(null);
  const saveTimersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const dirtyRowIdsRef = useRef(new Set<number>());
  const inFlightRowIdsRef = useRef(new Set<number>());
  const rowSavePromisesRef = useRef(new Map<number, Promise<void>>());
  const activeWorkbookIdRef = useRef<string | null>(workbookId);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    servicesRef.current = services;
  }, [services]);

  useEffect(() => {
    servicesSavingRef.current = servicesSaving;
  }, [servicesSaving]);

  useEffect(() => {
    activeWorkbookIdRef.current = workbookId;
    saveTimersRef.current.forEach(timeoutHandle => {
      clearTimeout(timeoutHandle);
    });
    saveTimersRef.current.clear();
    dirtyRowIdsRef.current.clear();
    inFlightRowIdsRef.current.clear();
    rowSavePromisesRef.current.clear();
    rowsRef.current = [];
    setRows([]);
    servicesRef.current = null;
    servicesSavingRef.current = false;
    servicesVersionRef.current = 0;
    setServices(null);
    setServicesSaving(false);
    setDirtyRowIds([]);
    setPendingRowIds([]);
    setLastSaveError(null);
    setActiveCellSampleId(null);
  }, [workbookId]);

  useEffect(() => {
    return () => {
      saveTimersRef.current.forEach(timeoutHandle => {
        clearTimeout(timeoutHandle);
      });
      saveTimersRef.current.clear();
      rowSavePromisesRef.current.clear();
    };
  }, []);

  const pendingRowIdSet = useMemo(() => new Set(pendingRowIds), [pendingRowIds]);
  const disableEditing = readOnly.workbook || viewerAccess.isCustomerOnly;

  useEffect(() => {
    if (activeCellSampleId == null) {
      return;
    }
    if (!rows.some(row => row.sampleId === activeCellSampleId)) {
      setActiveCellSampleId(null);
    }
  }, [activeCellSampleId, rows]);

  const setRowDirtyState = useCallback((sampleId: number, dirty: boolean) => {
    if (dirty) {
      dirtyRowIdsRef.current.add(sampleId);
    } else {
      dirtyRowIdsRef.current.delete(sampleId);
    }
    setDirtyRowIds(Array.from(dirtyRowIdsRef.current));
  }, []);

  const applySummaryToLocalState = useCallback(
    (summary: { services: LabWorkbookService[] | null; rows: LabDnaStepRow[] }) => {
      const validRowIds = new Set(summary.rows.map(row => row.sampleId));
      dirtyRowIdsRef.current.forEach(sampleId => {
        if (!validRowIds.has(sampleId)) {
          dirtyRowIdsRef.current.delete(sampleId);
        }
      });
      inFlightRowIdsRef.current.forEach(sampleId => {
        if (!validRowIds.has(sampleId)) {
          inFlightRowIdsRef.current.delete(sampleId);
        }
      });
      saveTimersRef.current.forEach((timeoutHandle, sampleId) => {
        if (!validRowIds.has(sampleId)) {
          clearTimeout(timeoutHandle);
          saveTimersRef.current.delete(sampleId);
        }
      });
      rowSavePromisesRef.current.forEach((_promise, sampleId) => {
        if (!validRowIds.has(sampleId)) {
          rowSavePromisesRef.current.delete(sampleId);
        }
      });
      rowsRef.current = summary.rows;
      servicesRef.current = summary.services;
      setRows(summary.rows);
      setServices(summary.services);
      setDirtyRowIds(Array.from(dirtyRowIdsRef.current));
      setPendingRowIds(current => current.filter(sampleId => validRowIds.has(sampleId)));
    },
    [],
  );

  const mergeServerSummaryIntoLocal = useCallback(
    (
      summary: { services: LabWorkbookService[] | null; rows: LabDnaStepRow[] },
      options?: { replaceSampleId?: number },
    ) => {
      if (rowsRef.current.length === 0) {
        return summary;
      }

      const protectedRowIds = new Set(dirtyRowIdsRef.current);
      if (options?.replaceSampleId != null) {
        protectedRowIds.delete(options.replaceSampleId);
      }

      const localRowById = new Map(rowsRef.current.map(row => [row.sampleId, row] as const));
      const serverRowById = new Map(summary.rows.map(row => [row.sampleId, row] as const));
      return {
        ...summary,
        rows: summary.rows.map(row =>
          protectedRowIds.has(row.sampleId)
            ? localRowById.get(row.sampleId) ?? row
            : serverRowById.get(row.sampleId) ?? row,
        ),
      };
    },
    [],
  );

  useEffect(() => {
    if (!dnaStepQuery.data) {
      return;
    }
    applySummaryToLocalState(mergeServerSummaryIntoLocal(dnaStepQuery.data));
  }, [applySummaryToLocalState, dnaStepQuery.data, mergeServerSummaryIntoLocal]);

  const reloadDnaStep = useCallback(async () => {
    const refreshed = await dnaStepQuery.refetch();
    if (refreshed.data) {
      applySummaryToLocalState(mergeServerSummaryIntoLocal(refreshed.data));
    }
  }, [applySummaryToLocalState, dnaStepQuery, mergeServerSummaryIntoLocal]);

  const flushRowSave = useCallback(
    async (sampleId: number): Promise<void> => {
      const existingPromise = rowSavePromisesRef.current.get(sampleId);
      if (existingPromise) {
        await existingPromise;
        return;
      }

      const requestWorkbookId = workbookId;
      if (!requestWorkbookId) {
        return;
      }

      const row = rowsRef.current.find(candidate => candidate.sampleId === sampleId);
      if (!row) {
        setRowDirtyState(sampleId, false);
        return;
      }

      const requestServicesVersion = servicesVersionRef.current;
      const savePromise = (async () => {
        inFlightRowIdsRef.current.add(sampleId);
        setPendingRowIds(current => (current.includes(sampleId) ? current : [...current, sampleId]));

        try {
          const summary = await saveLabDnaStep(requestWorkbookId, { rows: [buildRowUpdatePayload(row)] });
          if (activeWorkbookIdRef.current !== requestWorkbookId) {
            return;
          }
          const effectiveSummary = shouldPreserveLocalWorkbookServices(
            requestServicesVersion,
            servicesVersionRef.current,
            servicesSavingRef.current,
          )
            ? {
                ...summary,
                services: servicesRef.current,
              }
            : summary;
          const mergedSummary = mergeServerSummaryIntoLocal(effectiveSummary, { replaceSampleId: sampleId });
          setRowDirtyState(sampleId, false);
          queryClient.setQueryData(['labDnaStep', requestWorkbookId], mergedSummary);
          syncWorkbookServices(queryClient, requestWorkbookId, mergedSummary.services);
          void queryClient.invalidateQueries({ queryKey: ['labWorkbook', requestWorkbookId] });
          applySummaryToLocalState(mergedSummary);
          setLastSaveError(null);
        } catch (mutationError) {
          if (activeWorkbookIdRef.current !== requestWorkbookId) {
            return;
          }
          setRowDirtyState(sampleId, false);
          const message = toErrorMessage(mutationError, 'Failed to save DNA QC row');
          setLastSaveError(message);
          showToast({ status: 'error', title: message });
          await reloadDnaStep();
          throw mutationError;
        } finally {
          inFlightRowIdsRef.current.delete(sampleId);
          setPendingRowIds(current => current.filter(candidate => candidate !== sampleId));
          rowSavePromisesRef.current.delete(sampleId);
        }
      })();

      rowSavePromisesRef.current.set(sampleId, savePromise);
      await savePromise;
    },
    [
      applySummaryToLocalState,
      mergeServerSummaryIntoLocal,
      queryClient,
      reloadDnaStep,
      setRowDirtyState,
      showToast,
      workbookId,
    ],
  );

  const scheduleRowSave = useCallback(
    (sampleId: number) => {
      const existingTimeout = saveTimersRef.current.get(sampleId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const timeoutHandle = setTimeout(() => {
        saveTimersRef.current.delete(sampleId);
        void flushRowSave(sampleId);
      }, SAVE_DEBOUNCE_MS);
      saveTimersRef.current.set(sampleId, timeoutHandle);
    },
    [flushRowSave],
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<LabDnaStepRow>) => {
      const columnField =
        typeof event.colDef.field === 'string' && event.colDef.field.length > 0
          ? event.colDef.field
          : event.column.getColId();
      const fieldKey = columnField as DnaColumnField;
      const normalizedOldValue = normalizeComparableCellValue(fieldKey, event.oldValue);
      const normalizedNewValue = normalizeComparableCellValue(fieldKey, event.newValue);
      if (normalizedOldValue === normalizedNewValue) {
        return;
      }

      const nextRow = applyDerivedLabDnaStepDilutionFields({ ...event.data });
      const nextRows = rowsRef.current.map(row => (row.sampleId === nextRow.sampleId ? nextRow : row));
      rowsRef.current = nextRows;
      setRows(nextRows);
      setRowDirtyState(nextRow.sampleId, true);
      scheduleRowSave(nextRow.sampleId);
    },
    [scheduleRowSave, setRowDirtyState],
  );

  const handleServicesChange = useCallback(
    async (event: ChangeEvent<HTMLSelectElement>) => {
      const requestWorkbookId = workbookId;
      if (!requestWorkbookId) {
        return;
      }

      const nextValue = getServicesForChoiceValue(event.target.value);
      const requestServicesVersion = servicesVersionRef.current + 1;
      servicesVersionRef.current = requestServicesVersion;
      servicesRef.current = nextValue;
      servicesSavingRef.current = true;
      setServices(nextValue);
      setServicesSaving(true);

      try {
        const summary = await saveLabDnaStep(requestWorkbookId, {
          services: nextValue,
          rows: [],
        });
        if (activeWorkbookIdRef.current !== requestWorkbookId) {
          return;
        }
        if (requestServicesVersion !== servicesVersionRef.current) {
          return;
        }
        servicesSavingRef.current = false;
        const mergedSummary = mergeServerSummaryIntoLocal(summary);
        queryClient.setQueryData(['labDnaStep', requestWorkbookId], mergedSummary);
        syncWorkbookServices(queryClient, requestWorkbookId, summary.services);
        void queryClient.invalidateQueries({ queryKey: ['labWorkbook', requestWorkbookId] });
        applySummaryToLocalState(mergedSummary);
        setLastSaveError(null);
      } catch (mutationError) {
        if (activeWorkbookIdRef.current !== requestWorkbookId) {
          return;
        }
        if (requestServicesVersion !== servicesVersionRef.current) {
          return;
        }
        servicesSavingRef.current = false;
        setServicesSaving(false);
        const message = toErrorMessage(mutationError, 'Failed to save services');
        setLastSaveError(message);
        showToast({ status: 'error', title: message });
        await reloadDnaStep();
      } finally {
        if (requestServicesVersion === servicesVersionRef.current) {
          servicesSavingRef.current = false;
          setServicesSaving(false);
        }
      }
    },
    [applySummaryToLocalState, mergeServerSummaryIntoLocal, queryClient, reloadDnaStep, showToast, workbookId],
  );

  const flushQueuedRowSaves = useCallback(async () => {
    const queuedRowIds = new Set<number>([
      ...dirtyRowIdsRef.current,
      ...inFlightRowIdsRef.current,
      ...saveTimersRef.current.keys(),
    ]);

    if (queuedRowIds.size === 0) {
      return;
    }

    queuedRowIds.forEach(sampleId => {
      const timeoutHandle = saveTimersRef.current.get(sampleId);
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        saveTimersRef.current.delete(sampleId);
      }
    });

    await Promise.all(
      Array.from(queuedRowIds).map(async sampleId => {
        await flushRowSave(sampleId);
      }),
    );
  }, [flushRowSave]);

  const markReadyMutation = useMutation({
    mutationFn: async () => {
      if (!workbookId) {
        throw new Error('Missing workbook identifier');
      }
      return await markLabDnaStepReady(workbookId);
    },
    onSuccess: summary => {
      if (!workbookId) {
        return;
      }
      queryClient.setQueryData(['labWorkbook', workbookId], summary);
      void queryClient.invalidateQueries({ queryKey: ['labDnaStep', workbookId] });
      showToast({ status: 'success', title: 'Workbook marked ready for primer preparation' });
      setLastSaveError(null);
    },
    onError: mutationError => {
      const message = toErrorMessage(mutationError, 'Failed to mark DNA QC ready');
      setLastSaveError(message);
      showToast({ status: 'error', title: message });
    },
  });

  const handleMarkReady = useCallback(async () => {
    try {
      await flushQueuedRowSaves();
    } catch {
      return;
    }

    try {
      await markReadyMutation.mutateAsync();
    } catch {
      // Mutation errors are handled by the mutation callbacks.
    }
  }, [flushQueuedRowSaves, markReadyMutation]);

  const defaultColDef = useMemo<ColDef<LabDnaStepRow>>(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
      editable: false,
      suppressHeaderMenuButton: true,
      suppressKeyboardEvent: params => {
        const rawKey = (params.event as { key?: unknown } | undefined)?.key;
        const key = typeof rawKey === 'string' ? rawKey : null;
        if (!key || params.editing) {
          return false;
        }
        return key === 'Backspace' || key === 'Delete';
      },
    }),
    [],
  );

  const cellSelection = useMemo<CellSelectionOptions<LabDnaStepRow>>(
    () => ({
      handle: {
        mode: 'range',
      },
    }),
    [],
  );

  const copyRange = useCallback((api: GridApi<LabDnaStepRow>, includeHeaders: boolean): boolean => {
    const ranges = api.getCellRanges();
    if (!ranges || ranges.length === 0) {
      const focusedCell = api.getFocusedCell();
      if (!focusedCell || focusedCell.rowPinned != null) {
        return false;
      }
      api.clearCellSelection();
      api.addCellRange({
        rowStartIndex: focusedCell.rowIndex,
        rowEndIndex: focusedCell.rowIndex,
        columns: [focusedCell.column.getColId()],
      });
    }
    api.copySelectedRangeToClipboard({ includeHeaders });
    return true;
  }, []);

  const clearSelectedCells = useCallback((): boolean => {
    const api = gridRef.current?.api;
    if (!api) {
      return false;
    }

    const updatesBySampleId = new Map<number, Partial<LabDnaStepRow>>();
    const addClearedField = (sampleId: number, fieldKey: DnaColumnField) => {
      const meta = labDnaStepColumnMetadata[fieldKey];
      if (!meta.editable) {
        return;
      }
      const currentRow = rowsRef.current.find(candidate => candidate.sampleId === sampleId);
      const pendingUpdates = updatesBySampleId.get(sampleId);
      const previousRow = currentRow ? { ...currentRow, ...pendingUpdates } : null;
      if (!previousRow || previousRow[fieldKey] == null) {
        return;
      }
      const existing = updatesBySampleId.get(sampleId) ?? {};
      updatesBySampleId.set(sampleId, { ...existing, [fieldKey]: null });
    };

    const ranges = api.getCellRanges() ?? [];
    if (ranges.length > 0) {
      ranges.forEach((range: CellRange) => {
        const columns = range.columns;
        if (columns.length === 0) {
          return;
        }
        const startRow = range.startRow;
        const endRow = range.endRow ?? range.startRow;
        if (!startRow || !endRow || startRow.rowPinned != null || endRow.rowPinned != null) {
          return;
        }
        const startIndex = Math.min(startRow.rowIndex, endRow.rowIndex);
        const endIndex = Math.max(startRow.rowIndex, endRow.rowIndex);
        for (let rowIndex = startIndex; rowIndex <= endIndex; rowIndex += 1) {
          const rowNode = api.getDisplayedRowAtIndex(rowIndex);
          const rowData = rowNode?.data;
          if (!rowNode || !rowData) {
            continue;
          }
          columns.forEach(column => {
            const fieldId = resolveGridColumnFieldId(column);
            if (!fieldId) {
              return;
            }
            const fieldKey = fieldId as DnaColumnField;
            if (!(fieldKey in labDnaStepColumnMetadata) || !column.isCellEditable(rowNode)) {
              return;
            }
            addClearedField(rowData.sampleId, fieldKey);
          });
        }
      });
    } else {
      const focusedCell = api.getFocusedCell();
      if (!focusedCell || focusedCell.rowPinned != null) {
        return false;
      }
      const rowNode = api.getDisplayedRowAtIndex(focusedCell.rowIndex);
      const rowData = rowNode?.data;
      const fieldId = rowNode ? resolveGridColumnFieldId(focusedCell.column) : null;
      if (!rowNode || !rowData || !fieldId) {
        return false;
      }
      const fieldKey = fieldId as DnaColumnField;
      if (fieldKey in labDnaStepColumnMetadata && focusedCell.column.isCellEditable(rowNode)) {
        addClearedField(rowData.sampleId, fieldKey);
      }
    }

    if (updatesBySampleId.size === 0) {
      return false;
    }

    const nextRows = rowsRef.current.map(row => {
      const updates = updatesBySampleId.get(row.sampleId);
      return updates ? applyDerivedLabDnaStepDilutionFields({ ...row, ...updates }) : row;
    });
    rowsRef.current = nextRows;
    setRows(nextRows);
    updatesBySampleId.forEach((_updates, sampleId) => {
      setRowDirtyState(sampleId, true);
      scheduleRowSave(sampleId);
    });
    return true;
  }, [scheduleRowSave, setRowDirtyState]);

  const handleCellKeyDown = useCallback(
    (event: CellKeyDownEvent<LabDnaStepRow>) => {
      const keyboardEvent = event.event;
      if (!(keyboardEvent instanceof KeyboardEvent) || keyboardEvent.defaultPrevented) {
        return;
      }

      const api = event.api;
      const focusedRowIndex = event.rowIndex ?? null;
      const focusedColId = event.column.getColId();
      const editingCells = api.getEditingCells();
      const isEditingFocusedCell =
        editingCells.length > 0 &&
        editingCells.some(
          position =>
            position.rowPinned == null &&
            position.rowIndex === focusedRowIndex &&
            position.column?.getColId() === focusedColId,
        );

      if (isEditingFocusedCell && keyboardEvent.key === 'Enter') {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        api.stopEditing();
        if (!moveFocusRelative(api, focusedColId, focusedRowIndex, keyboardEvent.shiftKey ? -1 : 1, 0)) {
          api.setFocusedCell(focusedRowIndex ?? 0, focusedColId);
        }
        return;
      }

      if (!isEditingFocusedCell && keyboardEvent.key === 'Enter') {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        if (!moveFocusRelative(api, focusedColId, focusedRowIndex, keyboardEvent.shiftKey ? -1 : 1, 0)) {
          api.setFocusedCell(focusedRowIndex ?? 0, focusedColId);
        }
        return;
      }

      const isCutShortcut =
        !keyboardEvent.shiftKey &&
        !keyboardEvent.altKey &&
        (keyboardEvent.ctrlKey || keyboardEvent.metaKey) &&
        keyboardEvent.key.toLowerCase() === 'x';
      if (!isEditingFocusedCell && isCutShortcut) {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        const copied = copyRange(api, false);
        if (copied) {
          clearSelectedCells();
        }
        return;
      }

      if (!isEditingFocusedCell && (keyboardEvent.key === 'Backspace' || keyboardEvent.key === 'Delete')) {
        if (clearSelectedCells()) {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
        }
      }
    },
    [clearSelectedCells, copyRange],
  );

  const handleCellFocused = useCallback((event: CellFocusedEvent<LabDnaStepRow>) => {
    setActiveCellSampleId(getFocusedSampleId(event));
  }, []);

  const processCellForClipboard = useCallback((params: ProcessCellForExportParams<LabDnaStepRow>) => {
    const fieldId = resolveGridColumnFieldId(params.column);
    if (!fieldId || !(fieldId in labDnaStepColumnMetadata)) {
      const rawValue: unknown = params.value;
      return rawValue == null ? '' : String(rawValue);
    }
    return formatCellValueFromRaw(fieldId as DnaColumnField, params.value, params.node?.data);
  }, []);

  const processCellFromClipboard = useCallback((params: ProcessCellForExportParams<LabDnaStepRow>) => {
    const rowData = params.node?.data;
    const fieldId = resolveGridColumnFieldId(params.column);
    if (!rowData || !fieldId || !(fieldId in labDnaStepColumnMetadata)) {
      const clipboardValue: unknown = params.value;
      return clipboardValue;
    }
    const fieldKey = fieldId as DnaColumnField;
    const meta = labDnaStepColumnMetadata[fieldKey];
    const isEditable = params.node ? params.column.isCellEditable(params.node) : meta.editable;
    if (!meta.editable || !isEditable) {
      return rowData[fieldKey] ?? null;
    }
    return normalizeParsedCellValue(fieldKey, params.value);
  }, []);

  const visibleExtractionFieldKeys = useMemo(
    () =>
      labDnaStepExtractionFieldKeys.filter(fieldKey =>
        isDnaStepFieldVisible(fieldKey, services, viewerAccess.isCustomerOnly),
      ),
    [services, viewerAccess.isCustomerOnly],
  );
  const visibleQaFieldKeys = useMemo(
    () =>
      labDnaStepQaFieldKeys.filter(fieldKey => isDnaStepFieldVisible(fieldKey, services, viewerAccess.isCustomerOnly)),
    [services, viewerAccess.isCustomerOnly],
  );
  const visibleDilutionFieldKeys = useMemo(
    () =>
      labDnaStepDilutionFieldKeys.filter(fieldKey =>
        isDnaStepFieldVisible(fieldKey, services, viewerAccess.isCustomerOnly),
      ),
    [services, viewerAccess.isCustomerOnly],
  );

  const columnDefs = useMemo<Array<ColDef<LabDnaStepRow>>>(
    () => [
      ...visibleIdentityFieldKeys.map(fieldKey =>
        buildFieldColumn(fieldKey, { disableEditing, pendingRowIds: pendingRowIdSet, services }),
      ),
      ...visibleExtractionFieldKeys.map(fieldKey =>
        buildFieldColumn(fieldKey, { disableEditing, pendingRowIds: pendingRowIdSet, services }),
      ),
      ...visibleQaFieldKeys.map(fieldKey =>
        buildFieldColumn(fieldKey, { disableEditing, pendingRowIds: pendingRowIdSet, services }),
      ),
      ...visibleDilutionFieldKeys.map(fieldKey =>
        buildFieldColumn(fieldKey, { disableEditing, pendingRowIds: pendingRowIdSet, services }),
      ),
    ],
    [
      disableEditing,
      pendingRowIdSet,
      services,
      visibleDilutionFieldKeys,
      visibleExtractionFieldKeys,
      visibleQaFieldKeys,
    ],
  );

  if (!viewerAccess.canViewDnaQc) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription>You do not have access to this DNA QC step.</AlertDescription>
      </Alert>
    );
  }

  if (workbookQuery.isLoading || dnaStepQuery.isLoading) {
    return (
      <Center minH="320px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (workbookQuery.isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{toErrorMessage(workbookQuery.error, 'Failed to load workbook')}</AlertDescription>
      </Alert>
    );
  }

  if (workbookData && !workbookSupportsDnaStep) {
    return null;
  }

  if (dnaStepQuery.isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{toErrorMessage(dnaStepQuery.error, 'Failed to load DNA QC step')}</AlertDescription>
      </Alert>
    );
  }

  if (!workbook || !workbookData) {
    return null;
  }

  const workbookName = workbook.name ? workbook.name.trim() : '';
  const headerTitle = workbookName ? `${workbookName} — DNA QC` : 'DNA QC';
  const needsSamplesFirst =
    workbook.status === LabWorkbookStatus.DRAFT || workbook.status === LabWorkbookStatus.SAMPLE_SELECTION;
  const hasEditableRows = rows.some(row => row.sampleEditable);
  const canEditAnyRows = hasEditableRows && !disableEditing;
  const canMarkReady = workbook.status === LabWorkbookStatus.SUBMITTED && !disableEditing;
  const labSheetUrl = workbookData.sampleSheet.labSheetUrl;
  const channelUrl = workbook.channelUrl;
  const readyButtonDisabled =
    needsSamplesFirst ||
    !canMarkReady ||
    servicesSaving ||
    dirtyRowIds.length > 0 ||
    pendingRowIds.length > 0 ||
    markReadyMutation.isPending ||
    services == null;

  return (
    <VStack align="stretch" spacing={6}>
      <LabPageHeader
        title={headerTitle}
        subtitle="Capture DNA QC measurements plus extraction and dilution-service details."
        labSheetUrl={labSheetUrl}
        channelUrl={channelUrl}
        control={
          canMarkReady ? (
            <Button
              colorScheme="teal"
              onClick={() => {
                void handleMarkReady();
              }}
              isDisabled={readyButtonDisabled}
              isLoading={markReadyMutation.isPending}
            >
              Mark ready
            </Button>
          ) : undefined
        }
      />

      {needsSamplesFirst && !viewerAccess.isCustomerOnly ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Submit at least one chip&apos;s worth of samples before filling the DNA QC step.
          </AlertDescription>
        </Alert>
      ) : null}

      {!needsSamplesFirst &&
      workbook.status !== LabWorkbookStatus.SUBMITTED &&
      canEditAnyRows &&
      !viewerAccess.isCustomerOnly ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            This workbook has already progressed past DNA QC. Samples that are still SUBMITTED or UNAPPROVED remain
            editable here.
          </AlertDescription>
        </Alert>
      ) : null}

      {!needsSamplesFirst && !viewerAccess.isCustomerOnly && !canEditAnyRows ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            DNA QC is read only because all workbook samples are already past SUBMITTED or UNAPPROVED.
          </AlertDescription>
        </Alert>
      ) : null}

      {lastSaveError ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{lastSaveError}</AlertDescription>
        </Alert>
      ) : null}

      {viewerAccess.isCustomerOnly ? null : (
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', lg: 'flex-end' }}
          gap={4}
        >
          <FormControl maxW={{ base: '100%', lg: '340px' }}>
            <FormLabel htmlFor="lab-workbook-services">Services</FormLabel>
            <Select
              id="lab-workbook-services"
              value={getServicesChoiceValue(services)}
              onChange={event => {
                void handleServicesChange(event);
              }}
              isDisabled={needsSamplesFirst || !canEditAnyRows || servicesSaving || viewerAccess.isCustomerOnly}
            >
              <option value="">Select services</option>
              {labWorkbookServiceChoices.map(choice => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </Select>
          </FormControl>

          <VStack align={{ base: 'flex-start', lg: 'flex-end' }} spacing={1}>
            <Text color="gray.600" fontSize="sm">
              {pendingRowIds.length > 0
                ? `Saving ${pendingRowIds.length} row${pendingRowIds.length === 1 ? '' : 's'}...`
                : dirtyRowIds.length > 0
                  ? `Queued ${dirtyRowIds.length} unsaved row${dirtyRowIds.length === 1 ? '' : 's'}...`
                  : servicesSaving
                    ? 'Saving services...'
                    : ''}
            </Text>
          </VStack>
        </Flex>
      )}

      <Box
        borderRadius={8}
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
        sx={{
          [`& .${GRID_CLASS_NAME} .ag-header-cell.dna-tube-label-header`]: {
            cursor: 'default',
          },
          [`& .${GRID_CLASS_NAME} .ag-header-cell.dna-tube-label-header .ag-header-cell-text`]: {
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.02em',
            lineHeight: 1.15,
            textAlign: 'center',
            textTransform: 'uppercase',
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-tube-label-cell`]: {
            backgroundColor: GROUP_STYLES.tubeLabel.background,
            color: '#718096',
            cursor: 'default',
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-identity-cell`]: {
            backgroundColor: GROUP_STYLES.identity.background,
          },
          [`& .${GRID_CLASS_NAME} .ag-header-cell.dna-extraction-header`]: {
            backgroundColor: GROUP_STYLES.extraction.background,
          },
          [`& .${GRID_CLASS_NAME} .ag-header-cell.dna-qa-header`]: {
            backgroundColor: GROUP_STYLES.qa.background,
          },
          [`& .${GRID_CLASS_NAME} .ag-header-cell.dna-dilution-header`]: {
            backgroundColor: GROUP_STYLES.dilution.background,
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-extraction-cell`]: {
            backgroundColor: GROUP_STYLES.extraction.background,
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-qa-cell`]: {
            backgroundColor: GROUP_STYLES.qa.background,
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-dilution-cell`]: {
            backgroundColor: GROUP_STYLES.dilution.background,
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-read-only-cell`]: {
            color: '#475467',
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-derived-cell`]: {
            color: '#667085',
            cursor: 'default',
            fontWeight: 600,
            fontStyle: 'italic',
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-row-pending .ag-cell`]: {
            opacity: 0.7,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-tube-label-cell`]: {
            backgroundColor: GROUP_STYLES.tubeLabel.activeBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-identity-cell`]: {
            backgroundColor: GROUP_STYLES.identity.activeBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-extraction-cell`]: {
            backgroundColor: GROUP_STYLES.extraction.activeBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-qa-cell`]: {
            backgroundColor: GROUP_STYLES.qa.activeBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-dilution-cell`]: {
            backgroundColor: GROUP_STYLES.dilution.activeBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-tube-label-cell.ag-cell-focus`]: {
            backgroundColor: GROUP_STYLES.tubeLabel.activeCellBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-identity-cell.ag-cell-focus`]: {
            backgroundColor: GROUP_STYLES.identity.activeCellBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-extraction-cell.ag-cell-focus`]: {
            backgroundColor: GROUP_STYLES.extraction.activeCellBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-qa-cell.ag-cell-focus`]: {
            backgroundColor: GROUP_STYLES.qa.activeCellBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-dilution-cell.ag-cell-focus`]: {
            backgroundColor: GROUP_STYLES.dilution.activeCellBackground,
          },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-dilution-desired-final-volume-warning-cell`]: {
            backgroundColor: '#fff5f5',
            borderColor: '#fc8181',
          },
          [`& .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-dilution-cell.dna-dilution-desired-final-volume-warning-cell`]:
            {
              backgroundColor: '#fff5f5',
              borderColor: '#fc8181',
            },
          [`& .${GRID_CLASS_NAME} .ag-cell.dna-dilution-desired-final-volume-warning-cell.ag-cell-focus,
            & .${GRID_CLASS_NAME} .ag-cell.dna-dilution-desired-final-volume-warning-cell.ag-cell-range-selected,
            & .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-dilution-cell.dna-dilution-desired-final-volume-warning-cell.ag-cell-focus,
            & .${GRID_CLASS_NAME} .ag-row.dna-active-row .ag-cell.dna-dilution-cell.dna-dilution-desired-final-volume-warning-cell.ag-cell-range-selected`]:
            {
              backgroundColor: '#fed7d7',
              borderColor: '#e53e3e',
            },
        }}
      >
        <Box width="100%" height="70vh" className={`${GRID_CLASS_NAME} sample-sheet-grid`}>
          <AgGridReact<LabDnaStepRow>
            ref={gridRef}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={themeQuartz}
            getRowId={params => String(params.data.sampleId)}
            headerHeight={48}
            rowHeight={25}
            cellSelection={cellSelection}
            tooltipShowDelay={0}
            tooltipHideDelay={10_000}
            stopEditingWhenCellsLoseFocus
            ensureDomOrder
            suppressDragLeaveHidesColumns
            suppressContextMenu
            processCellForClipboard={processCellForClipboard}
            processCellFromClipboard={processCellFromClipboard}
            suppressCutToClipboard
            onCellValueChanged={handleCellValueChanged}
            onCellFocused={handleCellFocused}
            onCellKeyDown={handleCellKeyDown}
            rowClassRules={{
              'dna-row-pending': params => (params.data ? pendingRowIdSet.has(params.data.sampleId) : false),
              'dna-active-row': params => params.data?.sampleId === activeCellSampleId,
            }}
          />
        </Box>
      </Box>
    </VStack>
  );
}
